import styled, { keyframes } from 'styled-components';

import { ds } from '@/config/colors';

import { media } from '../../utils/mediaQueries';

const slideIn = keyframes`
  from {
    transform: translateX(100%);
  }
  to {
    transform: translateX(0);
  }
`;

const slideOut = keyframes`
  from {
    transform: translateX(0);
  }
  to {
    transform: translateX(100%);
  }
`;

export const Flag = styled.img`
  display: flex;
  align-items: center;
  justify-content: center;
  object-fit: cover;
`;

export const TriggerWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.8;
  }
`;

export const PreviewLabelText = styled.span`
  ${media.mobile} {
    display: none;
  }
`;

export const PreviewLabel = styled.span`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 400;
  color: ${({ theme }) => (theme.mode === 'dark' ? '#9ca3af' : '#6b7280')};
  white-space: nowrap;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    left: 22px;
    right: 0;
    bottom: -2px;
    height: 1px;
    background: ${({ theme }) => (theme.mode === 'dark' ? '#9ca3af' : '#6b7280')};
    transform: scaleX(0);
    transform-origin: right;
    transition: transform 0.2s ease;
  }

  ${TriggerWrapper}:hover &::after {
    transform: scaleX(1);
    transform-origin: left;
  }
`;

export const PreviewFlagWrapper = styled.div`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

export const ChangeLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  font-size: 12px;
  font-weight: 500;
  color: ${({ theme }) => (theme.mode === 'dark' ? '#9ca3af' : '#6b7280')};
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 2px;
  transition: color 0.2s ease;

  &:hover {
    color: ${ds.accentInk};
  }
`;

export const TriggerButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 30px;
  padding: 0 10px;
  border-radius: 6px;
  border: 1px solid ${({ theme }) => (theme.mode === 'dark' ? '#374151' : '#dcdfe4')};
  background: ${({ theme }) => (theme.mode === 'dark' ? '#242a33' : '#ffffff')};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${({ theme }) => (theme.mode === 'dark' ? '#4b5563' : '#d1d5db')};
    background: ${({ theme }) => (theme.mode === 'dark' ? '#374151' : '#f9fafb')};
  }

  &:active {
    transform: scale(0.98);
  }
`;

export const FlagWrapper = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

export const ButtonText = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => (theme.mode === 'dark' ? '#f8fafc' : '#1a1d1f')};
`;

export const Backdrop = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 9998;
  opacity: ${(props) => (props.$isOpen ? 1 : 0)};
  visibility: ${(props) => (props.$isOpen ? 'visible' : 'hidden')};
  transition: opacity 0.3s ease, visibility 0.3s ease;
`;

export const SlidePanel = styled.div<{ $isOpen: boolean; $isClosing: boolean }>`
  position: fixed;
  top: 0;
  right: 0;
  width: 100%;
  max-width: 450px;
  height: 100vh;
  background: ${({ theme }) => (theme.mode === 'dark' ? '#1a1f26' : '#ffffff')};
  box-shadow: ${({ theme }) => (theme.mode === 'dark'
    ? '-4px 0 24px rgba(255, 255, 255, 0.06)'
    : '-4px 0 24px rgba(224, 123, 45, 0.15)')};
  z-index: 9999;
  display: flex;
  flex-direction: column;
  animation: ${(props) => (props.$isClosing ? slideOut : slideIn)} 0.3s ease-out forwards;

  ${media.mobile} {
    max-width: 320px;
    border-radius: 6px 0 0 6px;
  }
`;

export const PanelHeader = styled.div`
  padding: 24px;
  border-bottom: 1px solid ${({ theme }) => (theme.mode === 'dark' ? '#374151' : '#dcdfe4')};
  flex-shrink: 0;

  ${media.mobile} {
    padding: 20px 16px;
  }
`;

export const PanelTitle = styled.h2`
  font-size: 22px;
  font-weight: 500;
  letter-spacing: -0.01em;
  color: ${({ theme }) => (theme.mode === 'dark' ? '#f8fafc' : ds.textPrimary)};
  margin: 0 0 8px 0;
  display: flex;
  align-items: center;
  gap: 10px;

  ${media.mobile} {
    font-size: 18px;
    gap: 8px;
  }
`;

export const PanelDescription = styled.p`
  font-size: 15px;
  font-weight: 400;
  color: ${({ theme }) => (theme.mode === 'dark' ? '#9ca3af' : ds.textSecondary)};
  line-height: 1.5;
  margin: 0;

  ${media.mobile} {
    font-size: 13px;
  }
`;

export const CloseButton = styled.button`
  position: absolute;
  top: 24px;
  right: 24px;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  color: ${({ theme }) => (theme.mode === 'dark' ? '#9ca3af' : '#6b7280')};
  transition: all 0.2s ease;

  &:hover {
    background: ${({ theme }) => (theme.mode === 'dark' ? '#374151' : '#f3f4f6')};
    color: ${({ theme }) => (theme.mode === 'dark' ? '#f8fafc' : '#1a1d1f')};
  }

  &:active {
    transform: scale(0.95);
  }

  ${media.mobile} {
    top: 16px;
    right: 12px;
    width: 28px;
    height: 28px;
  }
`;

export const PanelBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 20px 24px;

  ${media.mobile} {
    padding: 16px;
    padding-bottom: 24px;
  }
`;

export const LanguageList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  overflow: visible;

  ${media.mobile} {
    gap: 6px;
    padding-bottom: 20px;
  }
`;

export const DefaultLanguageBadge = styled.span`
  position: absolute;
  top: -8px;
  left: 8px;
  font-size: 9px;
  font-weight: 600;
  letter-spacing: 0.04em;
  padding: 2px 6px;
  background: ${ds.accentInk};
  color: white;
  border-radius: 3px;
  text-transform: uppercase;
  white-space: nowrap;
  box-shadow: none;
  line-height: 1.3;
  z-index: 10;

  ${media.mobile} {
    font-size: 8px;
    padding: 2px 5px;
    top: -7px;
    left: 6px;
  }
`;

/* Wiersz języka: mały radius (4 px), aktywny = ciemna ramka accent-ink na bieli
   (jak zaznaczenie w reszcie panelu), bez turkusowego tła i bez unoszenia na hover. */
export const LanguageButton = styled.button<{ $isSelected?: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  gap: 14px;
  width: 100%;
  padding: 12px 16px;
  border: 1px solid ${({ $isSelected, theme }) => {
    if ($isSelected) return theme.mode === 'dark' ? '#f3f4f6' : ds.accentInk;
    return theme.mode === 'dark' ? '#374151' : ds.borderDefault;
  }};
  box-shadow: ${({ $isSelected, theme }) => ($isSelected
    ? `inset 0 0 0 1px ${theme.mode === 'dark' ? '#f3f4f6' : ds.accentInk}`
    : 'none')};
  border-radius: 4px;
  background: ${({ theme }) => (theme.mode === 'dark' ? '#242a33' : '#ffffff')};
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease;
  overflow: visible;

  &:hover {
    background: ${({ theme }) => (theme.mode === 'dark' ? '#374151' : '#f9fafb')};
    border-color: ${({ $isSelected, theme }) => {
    if ($isSelected) return theme.mode === 'dark' ? '#f3f4f6' : ds.accentInk;
    return theme.mode === 'dark' ? '#4b5563' : ds.borderStrong;
  }};
  }

  ${media.mobile} {
    padding: 10px 12px;
    gap: 12px;
  }
`;

/* Flaga jako mały prostokąt z delikatnym zaokrągleniem i włosową ramką. */
export const LanguageFlagWrapper = styled.div`
  width: 30px;
  height: 22px;
  border-radius: 3px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 0 0 1px rgba(15, 23, 42, 0.08);

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 0;
  }

  ${media.mobile} {
    width: 26px;
    height: 19px;
  }
`;

export const LanguageName = styled.span`
  font-size: 15px;
  font-weight: 500;
  color: ${({ theme }) => (theme.mode === 'dark' ? '#f8fafc' : '#1a1d1f')};
  flex: 1;
  text-align: left;

  ${media.mobile} {
    font-size: 14px;
  }
`;

export const CheckIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: ${ds.accentInk};
  flex-shrink: 0;

  ${media.mobile} {
    width: 20px;
    height: 20px;
  }
`;

export const PanelFooter = styled.div`
  padding: 16px 24px;
  border-top: 1px solid ${({ theme }) => (theme.mode === 'dark' ? '#374151' : '#dcdfe4')};
  background: ${({ theme }) => (theme.mode === 'dark' ? '#1a1f26' : '#f9fafb')};
  flex-shrink: 0;

  ${media.mobile} {
    padding: 12px 16px;
  }
`;

export const FooterText = styled.p`
  font-size: 13px;
  color: ${({ theme }) => (theme.mode === 'dark' ? '#9ca3af' : '#6b7280')};
  line-height: 1.5;
  margin: 0;

  ${media.mobile} {
    font-size: 12px;
  }
`;

export const FooterLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  font-size: 13px;
  font-weight: 500;
  color: ${ds.accentInk};
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 2px;
  transition: color 0.2s ease;

  &:hover {
    color: #0f3333;
  }

  ${media.mobile} {
    font-size: 12px;
  }
`;
